/**
 * Off-canvas Menu System
 */

export const initMenu = () => {
  const menuToggle = document.querySelector('.menu-toggle');
  const menuClose = document.querySelector('.menu-close');
  const closeWrapper = document.querySelector('.offcanvas-close-wrapper');
  const offcanvasMenu = document.getElementById('offcanvas-menu');
  const menuOverlay = document.querySelector('.offcanvas-bg-overlay');
  const body = document.body;

  /**
   * Close submenu with animation sequence:
   * 1. Add is-closing → submenuOut animation plays (280ms)
   * 2. After 280ms → remove submenu-open from menu (bg transition starts, with 0.3s delay in CSS)
   * 3. Clean up is-active & is-closing classes
   */
  const closeSubmenu = (callback) => {
    const activeItem =
      offcanvasMenu &&
      offcanvasMenu.querySelector('.menu-item-has-children.is-active');
    if (!activeItem) {
      if (callback) callback();
      return;
    }

    activeItem.classList.add('is-closing');

    setTimeout(() => {
      activeItem.classList.remove('is-active', 'is-closing');
      offcanvasMenu.classList.remove('submenu-open');
      if (callback) callback();
    }, 150); // matches submenuOut animation duration
  };

  if (menuToggle && offcanvasMenu && menuOverlay) {
    menuToggle.addEventListener('click', () => {
      offcanvasMenu.classList.add('is-open');
      body.classList.add('menu-open');
      menuOverlay.classList.add('is-open');
      if (closeWrapper) closeWrapper.classList.add('is-visible');

      // Update ARIA states and manage focus
      menuToggle.setAttribute('aria-expanded', 'true');
      offcanvasMenu.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        if (menuClose) menuClose.focus();
      }, 300); // Wait for transition
    });
  }

  // Sub-menu click handling
  if (offcanvasMenu) {
    const menuItemsWithChildren = offcanvasMenu.querySelectorAll(
      '.menu-item-has-children > a',
    );

    menuItemsWithChildren.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const parentLi = link.parentElement;
        const wasActive = parentLi.classList.contains('is-active');

        if (wasActive) {
          // Close current submenu with animation
          closeSubmenu();
        } else {
          // Close any other open submenu first, then open new one
          const currentActive = offcanvasMenu.querySelector(
            '.menu-item-has-children.is-active',
          );
          if (currentActive) {
            currentActive.classList.remove('is-active', 'is-closing');
          }
          parentLi.classList.add('is-active');
          offcanvasMenu.classList.add('submenu-open');
        }
      });
    });
  }

  const resetMenu = () => {
    const handleCloseFinal = () => {
      if (offcanvasMenu) {
        offcanvasMenu.classList.remove('is-open');
        offcanvasMenu.setAttribute('aria-hidden', 'true');
      }
      body.classList.remove('menu-open');
      if (menuOverlay) menuOverlay.classList.remove('is-open');
      if (closeWrapper) closeWrapper.classList.remove('is-visible');
      if (menuToggle) {
        menuToggle.setAttribute('aria-expanded', 'false');
        menuToggle.focus();
      }
    };

    // Close submenu with animation before hiding the whole menu
    closeSubmenu(() => {
      handleCloseFinal();
    });

    // If no submenu was open, close immediately
    if (offcanvasMenu && !offcanvasMenu.classList.contains('submenu-open')) {
      handleCloseFinal();
    }
  };

  if (menuClose) {
    menuClose.addEventListener('click', resetMenu);
  }

  if (menuOverlay) {
    menuOverlay.addEventListener('click', resetMenu);
  }

  const getFocusableElements = (container) => {
    const candidateSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const candidates = Array.from(container.querySelectorAll(candidateSelector));
    return candidates.filter(el => {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }

      // Check if element is inside a submenu that is not active
      const submenu = el.closest('.sub-menu');
      if (submenu) {
        const parentLi = submenu.closest('.menu-item-has-children');
        if (parentLi && !parentLi.classList.contains('is-active')) {
          return false;
        }
      }

      // Check if element is inside language switcher dropdown that is not active
      const langDropdown = el.closest('.lang-dropdown');
      if (langDropdown) {
        const switcher = langDropdown.closest('.offcanvas-language-switcher');
        if (switcher && !switcher.classList.contains('is-open') && !switcher.matches(':focus-within')) {
          return false;
        }
      }

      let parent = el.parentElement;
      while (parent && parent !== container) {
        const parentStyle = window.getComputedStyle(parent);
        if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') {
          return false;
        }
        parent = parent.parentElement;
      }
      return true;
    });
  };

  // Close menu on Escape key / Manage focus trap on Tab key
  document.addEventListener('keydown', (e) => {
    if (!offcanvasMenu || !offcanvasMenu.classList.contains('is-open')) {
      return;
    }

    if (e.key === 'Escape') {
      resetMenu();
      return;
    }

    if (e.key === 'Tab') {
      const focusableElements = getFocusableElements(offcanvasMenu);
      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!offcanvasMenu.contains(document.activeElement)) {
        firstElement.focus();
        e.preventDefault();
        return;
      }

      if (e.shiftKey) {
        // Shift + Tab (backwards)
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        // Tab (forwards)
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    }
  });
  // ─── Language Switchers Toggles (Header & Offcanvas) ──────────────────
  document.querySelectorAll('.language-switcher, .offcanvas-language-switcher').forEach((switcher) => {
    const langCurrent = switcher.querySelector('.lang-current');
    if (!langCurrent) return;

    langCurrent.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = switcher.classList.contains('is-open');
      if (isOpen) {
        switcher.classList.remove('is-open');
        switcher.classList.add('is-closed-by-click');
        langCurrent.setAttribute('aria-expanded', 'false');
      } else {
        switcher.classList.add('is-open');
        switcher.classList.remove('is-closed-by-click');
        langCurrent.setAttribute('aria-expanded', 'true');
      }
    });

    switcher.addEventListener('mouseleave', () => {
      switcher.classList.remove('is-closed-by-click');
    });

    // Close switcher when clicking outside
    document.addEventListener('click', (e) => {
      if (!switcher.contains(e.target)) {
        switcher.classList.remove('is-open');
        langCurrent.setAttribute('aria-expanded', 'false');
      }
    });
  });
};
